import { useEffect } from 'react';
import { useT } from '~/i18n';
import PlanOffer from './PlanOffer';
import type { PartnerDiscount } from '../lib/plan';
import type { PlanCtaCase, PlanCtaSurface } from '../lib/planCta';

/**
 * El muro, en modal.
 *
 * Sólo aparece cuando el candidato PIDE algo que el plan gobierna después de
 * haber visto sus tres demostraciones: un "Enviar" de más, o activar el envío
 * automático. La primera vez que se descubre el plan NO es acá — es la tarjeta
 * que se abre en el panel al completar la tercera. Un modal interrumpe; ese
 * momento es un descubrimiento, no una interrupción.
 *
 * Se puede cerrar de tres maneras (Escape, el fondo, "Ahora no") a propósito:
 * un muro sin salida convierte una decisión en una trampa, y el candidato
 * todavía tiene tres correos suyos que mirar antes de decidir.
 */
interface Props {
  open: boolean;
  onClose: () => void;
  /** El llamado que corresponde, del servidor (`planCtaCase`). */
  ctaCase: PlanCtaCase;
  /** Desde dónde se abrió: 'nudge' (aviso al abrir la app) o 'paywall' (un «Enviar» de más). */
  surface?: Extract<PlanCtaSurface, 'nudge' | 'paywall' | 'manual_sheet' | 'missed_week' | 'jobs_for_you'>;
  /** El descuento del socio que lo trajo; ver `PlanOffer`. */
  partnerDiscount?: PartnerDiscount | null;
  /** Ver `PlanOffer`: el checkout dice que la suscripción sigue viva. */
  onAlreadySubscribed?: () => void;
  /** Otro titular: el aviso al abrir la app (`PlanNudge`) dice por qué aparece. */
  headline?: string;
  lede?: string;
}

export default function PaywallModal({ open, onClose, ctaCase, surface = 'paywall', onAlreadySubscribed, partnerDiscount = null, headline, lede }: Props) {
  const t = useT('plan');
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal modal-open" role="dialog" aria-modal="true" aria-label={t.paywall.label}>
      <div className="modal-box" style={{ maxWidth: '32rem' }}>
        <PlanOffer
          framed={false}
          headline={headline ?? t.paywall.headline}
          lede={lede ?? t.paywall.lede}
          ctaCase={ctaCase}
          surface={surface}
          partnerDiscount={partnerDiscount}
          onAlreadySubscribed={onAlreadySubscribed}
        />
        <div className="modal-action">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            {t.paywall.notNow}
          </button>
        </div>
      </div>
      <button className="modal-backdrop" aria-label={t.paywall.close} onClick={onClose} />
    </div>
  );
}
